// src/pages/Overview.tsx
import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useGuild } from '../lib/GuildContext';
import { formatGuildName } from '../lib/formatGuildName';
import { apiFetch } from '../lib/api';
import { useCommands } from '../features/commands/useCommands';
import { useMarketAlerts } from '../features/marketAlerts/useMarketAlerts';
import { useClanEvents } from '../features/clanEvents/useClanEvents';
import { useScheduledReports } from '../features/scheduledReports/useScheduledReports';
import { useLogs } from '../features/logs/useLogs';
import { summarize } from '../features/logs/summarize';
import { formatRelative } from '../lib/formatTime';
import './Overview.css';

interface OverviewStats {
  topCommands: { command: string; total: string }[];
  totalCommandsUsed: number;
  joinedAt: string | null;
}

function useOverviewStats(guildId: string | null) {
  return useQuery({
    queryKey: ['overview-stats', guildId],
    queryFn: () => apiFetch<OverviewStats>(`/api/guilds/${guildId}/overview-stats`),
    enabled: !!guildId,
  });
}

function useMarketSyncStatus() {
  return useQuery({
    queryKey: ['market-sync-status'],
    queryFn: () => apiFetch<{ lastSyncedAt: string | null }>('/api/market-sync-status'),
    refetchInterval: 30_000,
  });
}

interface LaunchCardProps {
  to: string;
  label: string;
  status: ReactNode;
  loading?: boolean;
}

function LaunchCard({ to, label, status, loading }: LaunchCardProps) {
  return (
    <Link to={to} className="launch-card">
      <p className="launch-card__label">{label}</p>
      <p className="launch-card__status">{loading ? '…' : status}</p>
    </Link>
  );
}

// Deliberately not a Link - these are informational only, no page to
// send someone to (design confirmed 2026-07-15). Visually distinct
// from LaunchCard via the info-card modifier, so it doesn't read as
// clickable when it isn't.
function InfoCard({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="launch-card launch-card--info">
      <p className="launch-card__label">{label}</p>
      <div className="launch-card__status">{children}</div>
    </div>
  );
}

export function Overview() {
  const { guilds, selectedGuildId } = useGuild();
  const selectedGuild = guilds.find((g) => g.guild_id === selectedGuildId);

  const { data: commands, isLoading: commandsLoading } = useCommands(selectedGuildId);
  const { data: alerts, isLoading: alertsLoading } = useMarketAlerts(selectedGuildId);
  const { data: events, isLoading: eventsLoading } = useClanEvents(selectedGuildId);
  const { data: reports, isLoading: reportsLoading } = useScheduledReports(selectedGuildId);
  const { data: logsData, isLoading: logsLoading } = useLogs(selectedGuildId, 0, 4);
  const { data: overviewStats, isLoading: statsLoading } = useOverviewStats(selectedGuildId);
  const { data: syncStatus } = useMarketSyncStatus();

  const enabledCommands = commands?.filter((c) => c.enabled).length ?? 0;
  const totalCommands = commands?.length ?? 0;

  const activeAlerts = alerts?.filter((a) => a.enabled).length ?? 0;
  const runningEvents = events?.filter((e) => e.status === 'active' || e.status === 'ending').length ?? 0;
  const enabledReports = reports?.filter((r) => r.enabled).length ?? 0;

  const settings = (selectedGuild?.settings ?? {}) as { modRoleId?: string | null; blacklistedChannels?: string[] };
  const modRoleSet = !!settings.modRoleId;
  const blacklistCount = settings.blacklistedChannels?.length ?? 0;

  return (
    <>
      <div className="page-header">
        <p className="page-header__eyebrow">{selectedGuild ? formatGuildName(selectedGuild.guild_name, selectedGuild.guild_id) : 'No server selected'}</p>
        <h1 className="page-header__title">Overview</h1>
      </div>

      <div className="launch-grid">
        <LaunchCard
          to="/commands"
          label="Commands"
          loading={commandsLoading}
          status={`${enabledCommands} of ${totalCommands} enabled`}
        />
        <LaunchCard
          to="/market-alerts"
          label="Market Alerts"
          loading={alertsLoading}
          status={`${activeAlerts} active`}
        />
        <LaunchCard
          to="/clan-events"
          label="Clan Events"
          loading={eventsLoading}
          status={`${runningEvents} running`}
        />
        <LaunchCard
          to="/channels"
          label="Channels"
          status={
            <>
              {modRoleSet ? 'Mod role set' : 'No mod role set'}
              {' · '}
              {blacklistCount} blacklisted
            </>
          }
        />
        <LaunchCard
          to="/reports"
          label="Scheduled Reports"
          loading={reportsLoading}
          status={`${enabledReports} scheduled`}
        />
        <Link to="/logs" className="launch-card launch-card--feed">
          <p className="launch-card__label">Recent Activity</p>
          {logsLoading && <p className="launch-card__status">…</p>}
          {!logsLoading && (!logsData || logsData.entries.length === 0) && (
            <p className="launch-card__status">No recent changes</p>
          )}
          {!logsLoading && logsData && logsData.entries.length > 0 && (
            <ul className="launch-card__feed">
              {logsData.entries.map((entry) => (
                <li key={entry.id}>
                  <span className="launch-card__feed-time">{formatRelative(entry.created_at)}</span>
                  {' — '}
                  {summarize(entry)}
                </li>
              ))}
            </ul>
          )}
        </Link>

        <InfoCard label="Top Commands">
          {statsLoading && '…'}
          {!statsLoading && (!overviewStats || overviewStats.topCommands.length === 0) && 'No usage data yet'}
          {!statsLoading && overviewStats && overviewStats.topCommands.length > 0 && (
            <ul className="launch-card__feed">
              {overviewStats.topCommands.map((c) => (
                <li key={c.command}>/{c.command} — {c.total} use{c.total === '1' ? '' : 's'}</li>
              ))}
            </ul>
          )}
        </InfoCard>

        <InfoCard label="Market Data">
          {syncStatus?.lastSyncedAt ? `Synced ${formatRelative(syncStatus.lastSyncedAt)}` : 'Sync status unavailable'}
        </InfoCard>

        <InfoCard label="Server Info">
          {statsLoading && '…'}
          {!statsLoading && overviewStats && (
            <>
              {overviewStats.joinedAt ? `Bot added ${formatRelative(overviewStats.joinedAt)}` : 'Join date unavailable'}
              {' · '}
              {overviewStats.totalCommandsUsed} commands used total
            </>
          )}
        </InfoCard>
      </div>
    </>
  );
}
